import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Ticket } from "lucide-react";
import { adminListPromoCodes, adminCreatePromoCode, adminUpdatePromoCode, adminDeletePromoCode } from "../../../lib/api";
import { fmtDate } from "./ActionDialog";

export function PromoCodesAdminPanel({ tiers }) {
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        code: "",
        description: "",
        kind: "credits",
        credits: "",
        subscription_days: "",
        tier_id: "",
        max_uses: 0,
        per_user_limit: 1,
        expires_at: "",
    });

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            setCodes(await adminListPromoCodes());
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load codes");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const create = async (e) => {
        e?.preventDefault?.();
        if (!form.code.trim()) {
            toast.error("Code is required.");
            return;
        }
        const payload = {
            code: form.code.trim(),
            description: form.description.trim(),
            kind: form.kind,
            max_uses: parseInt(form.max_uses || 0, 10) || 0,
            per_user_limit: parseInt(form.per_user_limit || 1, 10) || 1,
            expires_at: form.expires_at || "",
        };
        if (form.kind === "credits") {
            payload.credits = parseInt(form.credits, 10);
            if (!payload.credits || payload.credits <= 0) {
                toast.error("Enter a positive credit amount.");
                return;
            }
        } else if (form.kind === "subscription_days") {
            payload.subscription_days = parseInt(form.subscription_days, 10);
            payload.tier_id = form.tier_id;
            if (!payload.subscription_days || payload.subscription_days <= 0) {
                toast.error("Enter a positive day count.");
                return;
            }
            if (!payload.tier_id) {
                toast.error("Pick a tier for subscription_days promo.");
                return;
            }
        }
        try {
            await adminCreatePromoCode(payload);
            toast.success(`Promo "${payload.code.toUpperCase()}" created`);
            setForm({
                code: "",
                description: "",
                kind: "credits",
                credits: "",
                subscription_days: "",
                tier_id: "",
                max_uses: 0,
                per_user_limit: 1,
                expires_at: "",
            });
            refresh();
        } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Couldn't create code");
        }
    };

    const toggleActive = async (p) => {
        try {
            await adminUpdatePromoCode(p.id, { active: !p.active });
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete code "${p.code}"?`)) return;
        try {
            await adminDeletePromoCode(p.id);
            toast.success("Code deleted");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <section
            className="mt-10 rounded-md border border-[#2A3655] bg-[#0F1B3A] p-5"
            data-testid="admin-promo-codes"
        >
            <div className="flex items-center gap-2 mb-4">
                <Ticket className="w-5 h-5 text-emerald-300" />
                <h3 className="text-lg sm:text-xl font-black tracking-tight text-white">
                    Promo codes
                </h3>
            </div>
            <p className="text-[12px] text-slate-400 mb-4 max-w-prose">
                Codes grant either bonus credits or free subscription time.
                Set <code className="bg-[#0A1128] px-1 py-0.5 rounded border border-[#2A3655] text-[11px]">max_uses</code> = 0 for unlimited use across all users, and{" "}
                <code className="bg-[#0A1128] px-1 py-0.5 rounded border border-[#2A3655] text-[11px]">per_user_limit</code> for how many times one user can redeem the same code (default 1).
            </p>

            <form
                onSubmit={create}
                className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3"
                data-testid="admin-promo-form"
            >
                <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="CODE (e.g. WELCOME50)"
                    className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm uppercase font-mono tracking-wider"
                />
                <select
                    value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm"
                >
                    <option value="credits">Adds credits</option>
                    <option value="subscription_days">Adds subscription days</option>
                </select>
                {form.kind === "credits" ? (
                    <input
                        type="number"
                        min="1"
                        value={form.credits}
                        onChange={(e) => setForm({ ...form, credits: e.target.value })}
                        placeholder="Credits to grant"
                        className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm"
                    />
                ) : (
                    <input
                        type="number"
                        min="1"
                        value={form.subscription_days}
                        onChange={(e) => setForm({ ...form, subscription_days: e.target.value })}
                        placeholder="Days to grant"
                        className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm"
                    />
                )}
                {form.kind === "subscription_days" ? (
                    <select
                        value={form.tier_id}
                        onChange={(e) => setForm({ ...form, tier_id: e.target.value })}
                        className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm"
                    >
                        <option value="">— Tier to grant —</option>
                        {tiers.map((t) => (
                            <option key={t.id} value={t.id}>{t.title}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        type="text"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Description (optional)"
                        className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm"
                    />
                )}
            </form>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-5">
                <input
                    type="number"
                    min="0"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    placeholder="Max uses (0 = ∞)"
                    className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm"
                />
                <input
                    type="number"
                    min="1"
                    value={form.per_user_limit}
                    onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })}
                    placeholder="Per-user limit"
                    className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm"
                />
                <input
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm"
                />
                <button
                    type="button"
                    onClick={create}
                    data-testid="admin-promo-create"
                    className="px-3 py-2 text-sm font-bold rounded-sm bg-emerald-500 hover:bg-emerald-400 text-[#0A1128]"
                >
                    Add code
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                        <tr className="border-b border-[#2A3655]">
                            <th className="text-left py-2">Code</th>
                            <th className="text-left py-2">Grants</th>
                            <th className="text-left py-2">Used</th>
                            <th className="text-left py-2">Expires</th>
                            <th className="text-left py-2">Status</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={6} className="py-4 text-center text-slate-500">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && codes.length === 0 && (
                            <tr>
                                <td colSpan={6} className="py-4 text-center text-slate-500">
                                    No promo codes yet — create one above.
                                </td>
                            </tr>
                        )}
                        {codes.map((p) => (
                            <tr key={p.id} className="border-b border-[#2A3655]/60 text-slate-200">
                                <td className="py-2 font-mono font-bold tracking-wider">{p.code}</td>
                                <td className="py-2">
                                    {p.kind === "credits"
                                        ? `+${p.credits} credits`
                                        : `+${p.subscription_days} days of ${(tiers.find((t) => t.id === p.tier_id) || {}).title || p.tier_id}`}
                                </td>
                                <td className="py-2">
                                    {p.used_count}
                                    {p.max_uses > 0 && <span className="text-slate-500"> / {p.max_uses}</span>}
                                </td>
                                <td className="py-2 text-[11px] text-slate-400">
                                    {p.expires_at ? fmtDate(p.expires_at) : "Never"}
                                </td>
                                <td className="py-2">
                                    <button
                                        type="button"
                                        onClick={() => toggleActive(p)}
                                        data-testid={`admin-promo-toggle-${p.id}`}
                                        className={`text-[11px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${
                                            p.active
                                                ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                                                : "border-slate-500/60 text-slate-400 bg-slate-500/10"
                                        }`}
                                    >
                                        {p.active ? "Active" : "Inactive"}
                                    </button>
                                </td>
                                <td className="py-2 text-right">
                                    <button
                                        type="button"
                                        onClick={() => remove(p)}
                                        data-testid={`admin-promo-delete-${p.id}`}
                                        className="text-slate-400 hover:text-rose-400"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}


// ----- Main tab -----
